import React, { useState } from 'react';
import HobtalkLoader from '../../components/common/HobtalkLoader';
import { cookingStrings, moduleCommonStrings } from '../../content/hobbyModules';

/**
 * Validates url.
 *
 * @param {*} value - Value to inspect or transform.
 * @returns {*} The derived operation result.
 */
function validateUrl(value) {
  if (!value.trim()) return cookingStrings.import.validation.required;
  try {
    const parsed = new URL(value);
    if (!['http:', 'https:'].includes(parsed.protocol)) {
      return cookingStrings.import.validation.invalidWebUrl;
    }
    return '';
  } catch {
    return cookingStrings.import.validation.invalidRecipeUrl;
  }
}

/**
 * Renders the import modal experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.onImport - Callback invoked when import occurs.
 * @param {*} props.onCancel - Callback invoked when cancel occurs.
 * @param {*} props.isImporting - Whether is importing is enabled.
 * @returns {JSX.Element} The rendered interface.
 */
export default function ImportModal({ onImport, onCancel, isImporting }) {
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');

  /**
   * Handles submit.
   *
   * @param {*} event - Browser or React event that triggered the action.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const handleSubmit = (event) => {
    event.preventDefault();
    const nextError = validateUrl(url);
    setError(nextError);
    if (nextError) return;
    onImport(url.trim());
  };

  return (
    <div className="modal-overlay">
      <form className="modal-content import-flow" onSubmit={handleSubmit}>
        {isImporting && <HobtalkLoader variant="overlay" label={cookingStrings.import.scanning} />}

        <div>
          <p className="eyebrow">{cookingStrings.import.title}</p>
          <p>{cookingStrings.import.detail}</p>
        </div>

        <div className="form-group">
          <input
            type="url"
            placeholder={cookingStrings.import.placeholder}
            value={url}
            onChange={(e) => {
              setUrl(e.target.value);
              setError('');
            }}
            aria-invalid={Boolean(error)}
            aria-label={cookingStrings.import.urlLabel}
            required
            autoFocus
          />
          {error && <p className="field-error">{error}</p>}
        </div>

        <div className="form-actions">
          <button type="button" className="btn-ghost" onClick={onCancel}>{moduleCommonStrings.actions.cancel}</button>
          <button className="btn-primary" type="submit" disabled={!url.trim() || isImporting}>
            {isImporting ? cookingStrings.import.scanning : cookingStrings.import.scanAction}
          </button>
        </div>
      </form>
    </div>
  );
}
